import { capitalize } from "portal/utils/strings";
import {
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Skeleton,
  Switch,
  TextField,
} from "@mui/material";
import { classes } from "portal/theme/theme";
import { ConfigNode } from "protos/config/api/config_service";
import { debounce } from "portal/utils/timing";
import {
  filterConfigTree,
  getParentPath,
  includeAncestors,
  indexConfigTree,
  SearchIndex,
  searchTree,
} from "portal/utils/configs";
import { GlobalHotKeys } from "react-hotkeys";
import { SimpleTreeView, TreeItem } from "@mui/x-tree-view";
import { useLatest } from "common/hooks/useLatest";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { withErrorBoundary } from "portal/components/ErrorBoundary";
import { without } from "portal/utils/arrays";
import ChangedIcon from "@mui/icons-material/ChangeHistoryOutlined";
import ClearIcon from "@mui/icons-material/ClearOutlined";
import CollapsedIcon from "@mui/icons-material/ExpandMoreOutlined";
import ExpandedIcon from "@mui/icons-material/ChevronRightOutlined";
import React, {
  FunctionComponent,
  memo,
  SyntheticEvent,
  useCallback,
  useMemo,
  useRef,
  useState,
  useTransition,
} from "react";

// don't auto-expand search results when there are more matches than this;
// expanding a huge result set makes the (non-virtualized) tree unusably slow
const MAX_SEARCH_EXPANSIONS = 50;

interface NodeProps {
  node: ConfigNode;
  path: string;
  changedPathsAncestors: ReadonlySet<string>;
}

export const TreeNode: FunctionComponent<NodeProps> = memo(function TreeNode({
  node,
  path,
  changedPathsAncestors,
}) {
  return (
    <TreeItem
      slotProps={{
        label: { className: "font-mono" },
        content: { className: "py-0 [&[data-selected]]:font-bold" },
      }}
      itemId={path}
      label={
        <span className="flex items-center">
          {changedPathsAncestors.has(path) && (
            <ChangedIcon color="warning" className="mr-2 text-xs" />
          )}
          {node.name}
        </span>
      }
    >
      {node.children.map((child) => {
        const childPath = `${path}/${child.name}`;
        return (
          <TreeNode
            key={childPath}
            node={child}
            path={childPath}
            changedPathsAncestors={changedPathsAncestors}
          />
        );
      })}
    </TreeItem>
  );
});

const TREE_SKELETON = (
  <>
    <Skeleton variant="text" className="w-10" />
    <Skeleton variant="text" className="w-16 ml-4" />
    <Skeleton variant="text" className="w-10 ml-4" />
    <Skeleton variant="text" className="w-12 ml-4" />
    <Skeleton variant="text" className="w-10" />
  </>
);

interface TreeProps {
  className?: string;
  config?: ConfigNode;
  initialShowChanged: boolean;
  onSelect: (path: string | null) => void;
  selectedPath: string;
  addedPaths: string[] | undefined;
  removedPaths: string[] | undefined;
  changedPathsAncestors: string[] | undefined;
  changedPathsTree?: ConfigNode;
  isTemplate?: boolean;
}

const _Tree: FunctionComponent<TreeProps> = ({
  className,
  config,
  initialShowChanged,
  onSelect,
  selectedPath,
  changedPathsAncestors,
  changedPathsTree,
  isTemplate = false,
}) => {
  const { t } = useTranslation();

  const { state } = useLocation();

  const [expanded, _setExpanded] = useState<string[]>(() =>
    includeAncestors([selectedPath])
  );

  const setExpanded = useCallback(
    (paths: string[], keepParent: boolean = true): void => {
      if (keepParent) {
        // keep parent of selected node expanded
        _setExpanded([...paths, getParentPath(selectedPath)]);
      } else {
        _setExpanded(paths);
      }
    },
    [selectedPath]
  );
  const setExpandedRef = useLatest(setExpanded);

  const searchIndex: SearchIndex = useMemo(
    () => indexConfigTree(config),
    [config]
  );
  const changedSearchIndex: SearchIndex = useMemo(
    () => indexConfigTree(changedPathsTree),
    [changedPathsTree]
  );
  const [showChanged, setShowChanged] = useState<boolean>(initialShowChanged);
  const activeConfig = showChanged ? changedPathsTree : config;
  const activeIndex = showChanged ? changedSearchIndex : searchIndex;
  const activeIndexRef = useLatest(activeIndex);
  const [savedExpanded, saveExpanded] = useState<string[]>([]);
  // searchInput is live text box; searchQuery is debounced
  const [searchInput, setSearchInput] = useState<string>(state?.search ?? "");
  const [searchQuery, setSearchQuery] = useState<string>(state?.search ?? "");
  // waiting on the debounce; isPending covers the tree re-render after it
  const [isWaiting, setWaiting] = useState<boolean>(false);
  const [isPending, startTransition] = useTransition();
  const isSearching = isWaiting || isPending;
  const debounceSearchState = useRef(0); // increment to cancel outstanding callback
  const finalizeSearchQuery = useMemo(
    () =>
      debounce((query, expectedState) => {
        if (debounceSearchState.current !== expectedState) {
          return;
        }
        setWaiting(false);

        // Search changed tree or full tree depending on showChanged toggle
        const resultPaths = searchTree(activeIndexRef.current, query);

        // Mark the resulting tree update as non-urgent so keystrokes in the
        // search box stay responsive while the tree re-renders
        startTransition(() => {
          setSearchQuery(query);

          // Reset set of expanded nodes when search query changes. Access
          // search index and `setExpanded` through refs instead of memo
          // dependencies, since changes to the config and selection
          // should not cause this to re-trigger.
          const setExpanded = setExpandedRef.current;
          if (resultPaths.length > MAX_SEARCH_EXPANSIONS) {
            // too many matches to usefully auto-expand; show them collapsed
            setExpanded([]);
          } else {
            setExpanded(
              includeAncestors(
                // slice off the root node because the tree doesn't know
                // about it
                resultPaths.map((path) => path.split("/").slice(1).join("/"))
              )
            );
          }
        });
      }),
    [activeIndexRef, setExpandedRef, startTransition]
  );

  const handleSearchChange = (value: string): void => {
    if (!value) {
      clearSearch();
      return;
    }
    // save expanded nodes so we can restore later
    if (value && !searchInput) {
      saveExpanded(expanded);
    }
    // update value
    setSearchInput(value);
    setWaiting(true);
    finalizeSearchQuery(value, ++debounceSearchState.current);
  };
  const clearSearch = (): void => {
    ++debounceSearchState.current;
    setSearchInput("");
    setWaiting(false);
    startTransition(() => {
      setSearchQuery("");
      setExpanded([selectedPath, ...savedExpanded], false);
    });
  };

  const filteredConfig = useMemo(() => {
    if (!searchQuery) {
      return activeConfig;
    }

    // Search changed tree or full tree depending on showChanged toggle
    const resultPaths = searchTree(activeIndex, searchQuery);
    return filterConfigTree(activeConfig, resultPaths);
  }, [activeConfig, activeIndex, searchQuery]);

  const changedPathsAncestorsSet = useMemo(
    () => new Set(changedPathsAncestors ?? []),
    [changedPathsAncestors]
  );

  // controlled tree
  const handleToggle = (
    _event: SyntheticEvent | null,
    nodeId: string,
    isExpanded: boolean
  ): void => {
    if (isExpanded) {
      setExpanded([...expanded, nodeId], false);
    } else {
      setExpanded(without(expanded, nodeId), false);
    }
  };
  const handleSelect = (
    _event: SyntheticEvent | null,
    itemId: string | null
  ): void => {
    onSelect(itemId);
  };
  const handleItemClick = (_event: React.MouseEvent, itemId: string): void => {
    onSelect(itemId);
  };
  const inputRef = useRef<HTMLInputElement>();

  if (!config) {
    return (
      <div className={classes(className, "flex")}>
        <div className="min-w-32">{TREE_SKELETON}</div>
        <div className="h-full flex-grow flex pl-8">
          <Skeleton variant="rectangular" className="h-8 w-40" />
          <Skeleton variant="rectangular" className="h-8 w-28 ml-2" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <GlobalHotKeys
        keyMap={{ FOCUS: ["/", "ctrl+f"] }}
        handlers={{
          FOCUS: (event) => {
            inputRef.current?.focus();
            event?.preventDefault();
          },
        }}
      />
      <TextField
        size="small"
        inputRef={inputRef}
        value={searchInput}
        onChange={(event) => handleSearchChange(event.target.value)}
        className="w-full mr-5"
        label={t("utils.actions.search", {
          subject: capitalize(t("models.configs.config_other")),
        })}
        slotProps={{
          input: {
            endAdornment: (
              <InputAdornment position="end">
                {isSearching && <CircularProgress size={16} />}
                <IconButton onClick={clearSearch} edge="end">
                  <ClearIcon
                    className={classes({
                      hidden: !searchInput,
                    })}
                  />
                </IconButton>
              </InputAdornment>
            ),
          },

          htmlInput: { className: "font-mono" },
        }}
      />
      {!isTemplate && (
        <FormControlLabel
          className="ml-1 text-sm"
          control={
            <Switch
              color={showChanged ? "warning" : "info"}
              size="small"
              checked={showChanged}
              onChange={(event, checked) => {
                setShowChanged(checked);
              }}
              name="changed"
            />
          }
          label={t("views.fleet.robots.config.onlyChanged")}
        />
      )}
      <div
        className={classes(
          "overflow-y-visible md:overflow-y-auto overflow-x-hidden basis-full md:basis-0 flex-grow-0 md:flex-grow",
          "transition-opacity",
          { "opacity-50": isSearching }
        )}
      >
        <SimpleTreeView
          className={classes(className)}
          // eslint-disable-next-line unicorn/no-null -- MUI X uses null for empty single-select
          selectedItems={selectedPath || null}
          expandedItems={expanded}
          onSelectedItemsChange={handleSelect}
          onItemClick={handleItemClick}
          onItemExpansionToggle={handleToggle}
          slots={{
            collapseIcon: CollapsedIcon,
            expandIcon: ExpandedIcon,
          }}
        >
          {filteredConfig?.children.map((child) => (
            <TreeNode
              key={child.name}
              path={child.name}
              node={child}
              changedPathsAncestors={changedPathsAncestorsSet}
            />
          ))}
        </SimpleTreeView>
      </div>
    </div>
  );
};

export const Tree = withErrorBoundary(
  { i18nKey: "views.fleet.robots.config.errors.failed" },
  _Tree
);
